import type { TFunction } from 'i18next'
import { match, P } from 'ts-pattern'
import type {
  GenerationModelDetailFragment,
  TrainingTask,
} from 'src/services/apollo/generated/graphql'
import dayjs from 'src/utils/dayjs'
import { isUserMultiLora } from 'src/utils/model'
import { TrainingTaskStatus } from '../ModelTypeChip'

export function getTrainingTaskLabelAndStyle(
  t: TFunction,
  trainingTask: TrainingTask,
) {
  return match(trainingTask)
    .with(
      {
        status: TrainingTaskStatus.running,
        extra: {
          progress: P.select('progress', P.number),
          estimatedTotalTime: P.select('estimatedTotalTime', P.number),
        },
      },
      ({ progress, estimatedTotalTime }) => ({
        label: `${progress}% / ${t('market:train-lora.model.estimated-time')}: ${dayjs.duration(estimatedTotalTime, 'ms').humanize()}`,
        className: 'bg-yellow-500',
      }),
    )
    .with(
      { status: TrainingTaskStatus.waiting },
      { status: TrainingTaskStatus.running },
      () => ({
        label: t('market:train-lora.model.waiting'),
        className: 'bg-pink-400',
      }),
    )
    .with({ status: TrainingTaskStatus.failed }, () => ({
      label: t('market:train-lora.model.failed'),
      className: 'bg-orange-500',
    }))
    .with({ status: TrainingTaskStatus.completed }, () => ({
      label: t('market:train-lora.model.completed'),
      className: 'bg-green-500',
    }))
    .otherwise(() => null)
}

export function getTrainingTasksFromModel(
  model?: Pick<
    GenerationModelDetailFragment,
    'loraTrainingTasks' | 'trainingTask' | 'type'
  >,
): TrainingTask[] {
  const tasks = isUserMultiLora(model?.type)
    ? model.loraTrainingTasks
    : [model?.trainingTask]
  return (tasks?.filter(v => !!v) as TrainingTask[]) ?? []
}

export function getIncompleteTrainingTasksFromModel(
  model?: Pick<
    GenerationModelDetailFragment,
    'loraTrainingTasks' | 'trainingTask' | 'type'
  >,
): TrainingTask[] {
  return getTrainingTasksFromModel(model).filter(
    t => t.status !== TrainingTaskStatus.completed,
  )
}
